// DepartmentManagement.js
import React, { useState, useEffect, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import "./DepartmentManagement.css";
import { appendProductionInstanceScope } from "../productionRequestScope";
import DepartmentUserAssignments from "./DepartmentUserAssignments";
import {
  findUserOption,
  getDepartmentManagerDisplayName,
  normalizeProductionUserOptions,
  normalizeUserId,
} from "./departmentUserOptions";

import { refreshProductionSidebar } from "../../../../utils/sidebarEvents";
// import { Pencil, Trash2, RotateCcw } from "lucide-react";
// import { Eye } from "lucide-react";

import {
  Pencil,
  Trash2,
  RotateCcw,
  Eye,
  Copy,
  Download,
  User,
  MapPin,
  Clock,
  DollarSign,
  Package,
  FileText,
  Building2,
  TrendingUp,
  Calendar,
  CheckCircle2,
  XCircle,
  Archive,
  RotateCw,
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
const fetch = (input, init = {}) => window.fetch(appendProductionInstanceScope(input, {
  pathname: window.location.pathname,
  apiBase: BASE_URL,
}), init);

// Utility functions for data formatting
const formatCurrency = (value) => {
  if (!value || value === 0) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getRelativeTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDateTime(dateString);
};

const getActionConfig = (action) => {
  const configs = {
    CREATE: { icon: CheckCircle2, color: "success", label: "Created" },
    UPDATE: { icon: TrendingUp, color: "info", label: "Updated" },
    ARCHIVE: { icon: Archive, color: "error", label: "Archived" },
    RESTORE: { icon: RotateCw, color: "warning", label: "Restored" },
  };
  return configs[action] || { icon: FileText, color: "default", label: action };
};

const handleCopyToClipboard = async (
  text,
  successMessage = "Copied to clipboard"
) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch (error) {
    toast.error("Failed to copy");
  }
};

const DepartmentManagement = ({ instanceUniqueId = "" }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [editingDept, setEditingDept] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    type: "Production",
    parentId: "",
    manager: "",
    costCenter: "",
    costPerHour: "",
    capacity: "",
    location: "",
    operatingHours: "",
    status: true,
    description: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [restoreId, setRestoreId] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewDept, setViewDept] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState("details"); // details | audit

  const [eligibleProductionUsers, setEligibleProductionUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [selectedProductionOperatorIds, setSelectedProductionOperatorIds] = useState([]);
  const [operatorMappingsLoading, setOperatorMappingsLoading] = useState(false);
  const [operatorMappingError, setOperatorMappingError] = useState("");
    
  // Auto-generate department code

  useEffect(() => {
    if (!formData.name || editingDept) return;

    const prefix = formData.name.trim().substring(0, 3).toUpperCase();

    const existingCodes = departments
      .map((d) => d.code)
      .filter((code) => typeof code === "string" && code.startsWith(prefix))
      .map((code) => parseInt(code.split("-")[1], 10) || 0);

    const nextNumber =
      existingCodes.length > 0 ? Math.max(...existingCodes) + 1 : 1;

    setFormData((prev) => ({
      ...prev,
      code: `${prefix}-${String(nextNumber).padStart(3, "0")}`,
    }));
  }, [formData.name, editingDept, departments]);

  // Search and Filter Logic
  const filteredAndSortedDepartments = useMemo(() => {
    let filtered = departments;

    // Search filter
        // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();

      filtered = filtered.filter((dept) => {
        const name = (dept.name || "").toLowerCase();
        const code = (dept.code || "").toLowerCase();
        const manager = getDepartmentManagerDisplayName(
          dept.manager,
          eligibleProductionUsers
        ).toLowerCase();
        const type = (dept.type || "").toLowerCase();

        return (
          name.includes(q) ||
          code.includes(q) ||
          manager.includes(q) ||
          type.includes(q)
        );
      });
    }


    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter(
        (dept) => dept.status.toLowerCase() === filterStatus
      );
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((dept) => dept.type === filterType);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue = sortColumn === "manager"
        ? getDepartmentManagerDisplayName(a.manager, eligibleProductionUsers)
        : a[sortColumn];
      let bValue = sortColumn === "manager"
        ? getDepartmentManagerDisplayName(b.manager, eligibleProductionUsers)
        : b[sortColumn];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    departments,
    searchQuery,
    sortColumn,
    sortDirection,
    filterStatus,
    filterType,
    eligibleProductionUsers,
  ]);

  // Calculate stats
  const stats = useMemo(
    () => ({
      total: departments.length,
      active: departments.filter((d) => d.status === "Active").length,
      totalCapacity: departments.reduce((sum, d) => sum + (Number(d.capacity) || 0), 0),
      avgCost:
        departments.length > 0
          ? Math.round(
              departments.reduce((sum, d) => sum + (Number(d.costPerHour) || 0), 0) /
                departments.length
            )
          : 0,
    }),
    [departments]
  );

  // Handle Sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle Row Selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const toggleAllRows = () => {
    if (selectedRows.length === filteredAndSortedDepartments.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredAndSortedDepartments.map((d) => d.id));
    }
  };


const normalizeDepartment = (dept = {}) => ({
  id: dept.id,

  name: dept.name ?? "",
  code: dept.code ?? "",
  type: dept.type ?? "Production",
  status: dept.status ?? "Inactive",
  is_deleted: Boolean(dept.is_deleted),

  parentId: dept.parent_id ?? dept.parentId ?? "",

  manager: dept.manager ?? "",

  costCenter: dept.cost_center ?? dept.costCenter ?? "",
  costPerHour: dept.cost_per_hour ?? dept.costPerHour ?? "",
  capacity: dept.capacity ?? "",
  location: dept.location ?? "",
  operatingHours: dept.operating_hours ?? dept.operatingHours ?? "",
  description: dept.description ?? "",
  audit: dept.audit ?? null,
});




useEffect(() => {
  if (!showModal || !instanceUniqueId) return undefined;

  let cancelled = false;
  const controller = new AbortController();

  const fetchEligibleProductionUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError("");
      setEligibleProductionUsers([]);

      const scopedUsersUrl = appendProductionInstanceScope(
        `${BASE_URL}/production/users?role=production-rep`,
        {
          pathname: `/instance/${encodeURIComponent(instanceUniqueId)}/production/department-management`,
          apiBase: BASE_URL,
        }
      );

      const res = await window.fetch(
        scopedUsersUrl,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          signal: controller.signal,
        }
      );

      if (!res.ok) {
        throw new Error("Failed to load eligible Production reps");
      }

      const data = await res.json();
      if (!data?.success || !Array.isArray(data.users)) {
        throw new Error(data?.message || "Invalid Production users response");
      }

      if (!cancelled) {
        setEligibleProductionUsers(normalizeProductionUserOptions(data));
      }
    } catch (err) {
      if (cancelled || err.name === "AbortError") return;
      setEligibleProductionUsers([]);
      setUsersError("Unable to load Production reps");
      console.error("Production user picker failed:", err);
    } finally {
      if (!cancelled) setUsersLoading(false);
    }
  };

  fetchEligibleProductionUsers();
  return () => {
    cancelled = true;
    controller.abort();
  };
}, [showModal, instanceUniqueId]);

useEffect(() => {
  const departmentId = editingDept?.id;
  if (!showModal || !departmentId || !instanceUniqueId) return undefined;

  let cancelled = false;
  const controller = new AbortController();

  const fetchDepartmentOperators = async () => {
    setSelectedProductionOperatorIds([]);
    setOperatorMappingError("");
    setOperatorMappingsLoading(true);

    try {
      const scopedOperatorUrl = appendProductionInstanceScope(
        `${BASE_URL}/production/departments/${departmentId}/operators`,
        {
          pathname: `/instance/${encodeURIComponent(instanceUniqueId)}/production/department-management`,
          apiBase: BASE_URL,
        }
      );
      const res = await window.fetch(scopedOperatorUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load Production Operators");
      }
      if (!cancelled) {
        setSelectedProductionOperatorIds((data.operatorIds || []).map(Number));
      }
    } catch (error) {
      if (cancelled || error.name === "AbortError") return;
      const message = error.message || "Failed to load Production Operators";
      setOperatorMappingError(message);
      toast.error(message);
    } finally {
      if (!cancelled) setOperatorMappingsLoading(false);
    }
  };

  fetchDepartmentOperators();
  return () => {
    cancelled = true;
    controller.abort();
  };
}, [showModal, editingDept?.id, instanceUniqueId]);

  // Form Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = "Department name is required";
    if (!formData.type) errors.type = "Department type is required";
    // if (!formData.costPerHour || formData.costPerHour <= 0)
    //   errors.costPerHour = "Valid cost per hour is required";
    // if (!formData.capacity || formData.capacity <= 0)
    //   errors.capacity = "Valid capacity is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Submit

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (usersLoading || usersError) {
      toast.error(usersError || "Eligible Production reps are still loading");
      return;
    }
    if (operatorMappingsLoading || operatorMappingError) {
      toast.error(operatorMappingError || "Production Operators are still loading");
      return;
    }

    try {
      setLoading(true);

      

      const isEdit = Boolean(editingDept);

      const url = isEdit
        ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments/${editingDept.id}`
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments`;

      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          name: formData.name,
          code: formData.code,
          type: formData.type,
          status: formData.status ? "Active" : "Inactive",


            parentId: formData.parentId || null,
  manager: findUserOption(eligibleProductionUsers, formData.manager)?.id || null,
  costCenter: formData.costCenter || null,
  costPerHour: formData.costPerHour
    ? Number(formData.costPerHour)
    : null,
  capacity: formData.capacity
    ? Number(formData.capacity)
    : null,
  location: formData.location || null,
  operatingHours: formData.operatingHours || null,
  description: formData.description || null,

        }),
      });

      const data = await res.json();

      

      // showApiToast(data, "Department archived", "Failed to archive department");

      // if (data.success) {
      //   fetchDepartments(showArchived);
      //   refreshProductionSidebar();
      // }

      if (data.success) {
        const savedDepartmentId = editingDept?.id || data.data?.id;
        const operatorRes = await fetch(
          `${BASE_URL}/production/departments/${savedDepartmentId}/operators`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            body: JSON.stringify({
              operatorIds: selectedProductionOperatorIds,
            }),
          }
        );
        const operatorData = await operatorRes.json().catch(() => ({}));
        if (!operatorRes.ok || !operatorData.success) {
          if (!editingDept && savedDepartmentId) {
            // The department POST already succeeded. Retry as PATCH so a
            // transient mapping failure cannot create a duplicate department.
            setEditingDept({ id: savedDepartmentId });
          }
          toast.error(
            operatorData.message ||
              "Department saved, but Production Operators could not be saved"
          );
          fetchDepartments(showArchived);
          return;
        }
        toast.success(
          data.message || (editingDept ? "Department updated successfully" : "Department created successfully")
        );
        fetchDepartments(showArchived);
        refreshProductionSidebar();
        handleModalClose(); //  Close the modal
      } else {
        toast.error(data.message || "Failed to save department");
      }
    } catch (err) {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  // console.log("Editing dept:", editingDept);

  


  const handleEdit = (dept) => {
  if (dept.is_deleted) {
    toast.error("Archived department cannot be edited");
    return;
  }

  const normalized = normalizeDepartment(dept);

  setEditingDept(normalized);
  setFormData({
    name: normalized.name,
    code: normalized.code,
    type: normalized.type,
    parentId: normalized.parentId,
    manager: normalized.manager,
    costCenter: normalized.costCenter,
    costPerHour: normalized.costPerHour,
    capacity: normalized.capacity,
    location: normalized.location,
    operatingHours: normalized.operatingHours,
    status: normalized.status === "Active",
    description: normalized.description,
  });

  setSelectedProductionOperatorIds([]);
  setOperatorMappingError("");
  setShowModal(true);
};

  // Handle Delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments/${deleteId}/archive`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = await res.json();
      // if (data.success) {
      //   toast.success("Department archived");
      //   fetchDepartments();
      // }
      // showApiToast(data, "Department archived", "Failed to archive department");

      if (data.success) {
        fetchDepartments(showArchived);
        refreshProductionSidebar();
      }
    } catch {
      toast.error("Failed to archive");
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      type: "Production",
      parentId: "",
      manager: "",
      costCenter: "",
      costPerHour: "",
      capacity: "",
      location: "",
      operatingHours: "",
      status: true,
      description: "",
    });
    setFormErrors({});
    setEditingDept(null);
    setSelectedProductionOperatorIds([]);
    setOperatorMappingsLoading(false);
    setOperatorMappingError("");
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  useEffect(() => {
    fetchDepartments(showArchived);
  }, [showArchived]);

  const fetchDepartments = async (archived = false) => {
    try {
      setLoading(true);

      const res = await fetch(
        `${
          process.env.REACT_APP_NODEJS_SERVER_URL
        }/production/departments/manage/list${
          archived ? "?archived=true" : ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = await res.json();

      if (data.success) {
        // const mappedDepartments = data.data.map((dept) => ({
        //   id: dept.id,
        //   name: dept.name,
        //   code: dept.code,
        //   type: dept.type,
        //   status: dept.status, // "Active" | "Inactive"
        //   is_deleted: dept.is_deleted,
        //   parentId: dept.parent_id || null,

        //   // safe defaults
        //   manager: dept.manager || "-",
        //   costPerHour: dept.cost_per_hour || 0,
        //   capacity: dept.capacity || 0,
        //   location: dept.location || "-",
        //   operatingHours: dept.operating_hours || "",
        //   description: dept.description || "",
        // }));

        const mappedDepartments = data.data.map((dept) =>
  normalizeDepartment(dept)
);

        setDepartments(mappedDepartments);
      }
    } catch (err) {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentDetails = async (id) => {
    try {
      setViewLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments/manage/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || "Failed to load department details");
        return;
      }

      setViewDept(normalizeDepartment(data.data));
      setActiveViewTab("details");
      setShowViewModal(true);
    } catch (err) {
      toast.error("Unable to fetch department details");
    } finally {
      setViewLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments/${id}/restore`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = await res.json();

      

      showApiToast(data, "Department restored", "Restore failed");

      if (data.success) {
        fetchDepartments(showArchived);
        refreshProductionSidebar();
      }
    } catch {
      toast.error("Server error");
    }
  };



  // ESC key handler for all modals
useEffect(() => {
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      if (showModal) {
        handleModalClose();
      }
      if (showViewModal) {
        setShowViewModal(false);
        setViewDept(null);
      }
      if (showDeleteModal) {
        setShowDeleteModal(false);
      }
      if (showRestoreModal) {
        setShowRestoreModal(false);
      }
    }
  };

  
  document.addEventListener("keydown", handleEscapeKey);

  // Cleanup on unmount
  return () => {
    document.removeEventListener("keydown", handleEscapeKey);
  };
}, [showModal, showViewModal, showDeleteModal, showRestoreModal]);


  const showApiToast = (data, fallbackSuccess, fallbackError) => {
    if (data?.success) {
      toast.success(data.message || fallbackSuccess);
    } else {
      toast.error(data?.message || fallbackError);
    }
  };

  const accessToken = localStorage.getItem("accessToken");
  // console.log("accessToken BEING SENT:", accessToken);

  return (
    <div className="department-container">
      <Toaster position="top-right" />
      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Department Management</h1>
          <p className="page-subtitle">
            Manage your production departments and resources
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 3.33334V12.6667M3.33334 8H12.6667"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Create Department
        </button>
      </div>
      {/* STATS GRID */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Departments</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Departments</div>
          <div className="stat-value stat-success">{stats.active}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Capacity</div>
          <div className="stat-value">
            {stats.totalCapacity}
            <span className="stat-unit">units/day</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Cost Per Hour</div>
          <div className="stat-value">
            ${stats.avgCost}
            <span className="stat-unit">/hour</span>
          </div>
        </div>
      </div>
      {/* CONTROLS BAR */}
      <div className="controls-bar">
        {/* LEFT: SEARCH */}
        <div className="controls-left">
          <div className="search-box">
            <svg
              className="search-icon"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M15.75 15.75L12.4875 12.4875"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>

            <input
              type="text"
              className="search-input"
              placeholder="Search departments, codes, managers…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* CENTER: FILTERS */}
        <div className="controls-center">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Type: All</option>
            <option value="Production">Production</option>
            <option value="Quality">Quality</option>
            <option value="Finishing">Finishing</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Planning">Planning</option>
            <option value="Logistics">Logistics</option>
          </select>
        </div>

        {/* RIGHT: ARCHIVED TOGGLE */}
        <div className="controls-right">
          <label className="archive-toggle-pill">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span className="pill-slider"></span>
            <span className="pill-text">Archived</span>
          </label>
        </div>
      </div>
      {/* BULK ACTIONS */}
      {selectedRows.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">{selectedRows.length} selected</span>
          <div className="bulk-buttons">
            {/* <button className="btn-bulk">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.3333 2H14V4.66667M14 2L9.33333 6.66667M4.66667 14H2V11.3333M2 14L6.66667 9.33333"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Export
            </button> */}


            {/* <button 
  className="btn-bulk"
  onClick={() => {
    toast.loading("Export functionality coming soon", {
      duration: 2500,
    });
  }}
>
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.3333 2H14V4.66667M14 2L9.33333 6.66667M4.66667 14H2V11.3333M2 14L6.66667 9.33333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
  Export
</button> */}
            {/* <button
              className="btn-bulk btn-bulk-danger"
              onClick={() => setSelectedRows([])}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 4H14M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333M3.33333 4L4 12.6667C4 13.403 4.59695 14 5.33333 14H10.6667C11.403 14 12 13.403 12 12.6667L12.6667 4M6 4V2.66667C6 2.29848 6.29848 2 6.66667 2H9.33333C9.70152 2 10 2.29848 10 2.66667V4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Delete Selected
            </button> */}
            <button className="btn-bulk" onClick={() => setSelectedRows([])}>
              Clear Selection
            </button>
          </div>
        </div>
      )}
      {/* TABLE */}
      <div className="table-card">
        {filteredAndSortedDepartments.length > 0 ? (
          <table className="premium-table">
            <thead>
              <tr>
                <th className="th-checkbox">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={
                      selectedRows.length ===
                      filteredAndSortedDepartments.length
                    }
                    onChange={toggleAllRows}
                  />
                </th>
                <th className="sortable" onClick={() => handleSort("name")}>
                  <div className="th-content">
                    Department
                    {sortColumn === "name" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="sortable" onClick={() => handleSort("type")}>
                  <div className="th-content">
                    Type
                    {sortColumn === "type" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="sortable" onClick={() => handleSort("manager")}>
                  <div className="th-content">
                    Manager
                    {sortColumn === "manager" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="sortable" onClick={() => handleSort("status")}>
                  <div className="th-content">
                    Status
                    {sortColumn === "status" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort("costPerHour")}
                >
                  <div className="th-content">
                    Cost/Hour
                    {sortColumn === "costPerHour" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="sortable" onClick={() => handleSort("capacity")}>
                  <div className="th-content">
                    Capacity
                    {sortColumn === "capacity" && (
                      <svg
                        className={`sort-icon ${
                          sortDirection === "desc" ? "desc" : ""
                        }`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedDepartments.map((dept) => (
                <tr
                  key={dept.id}
                  className={selectedRows.includes(dept.id) ? "selected" : ""}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedRows.includes(dept.id)}
                      onChange={() => toggleRowSelection(dept.id)}
                    />
                  </td>
                  <td>
                    <div className="dept-info">
                      <span className="dept-name">{dept.name}</span>
                      <span className="dept-code">{dept.code}</span>
                    </div>
                  </td>
                  <td>
                    <span className="dept-type">{dept.type}</span>
                  </td>
                  <td>
                    <span className="text-medium">
                      {getDepartmentManagerDisplayName(dept.manager, eligibleProductionUsers)}
                    </span>
                  </td>
                  <td>
                    {/* <span
                      className={`status-badge ${dept.status.toLowerCase()}`}
                    >
                      <span className="status-dot"></span>
                      {dept.status}
                    </span> */}

                    <span
                      className={`status-badge ${
                        dept.is_deleted ? "archived" : dept.status.toLowerCase()
                      }`}
                    >
                      {dept.is_deleted ? "Archived" : dept.status}
                    </span>
                  </td>
                  <td>
                    <span className="text-medium">${dept.costPerHour}</span>
                  </td>
                  <td>
                    <span className="text-medium">{dept.capacity}/day</span>
                  </td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        title="View Department"
                        onClick={() => fetchDepartmentDetails(dept.id)}
                      >
                        <Eye size={16} />
                      </button>

                      {/* EDIT (only if not archived) */}
                      {!dept.is_deleted && (
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(dept)}
                          title="Edit Department"
                        >
                          {/* ✏️ */}
                          <Pencil size={16} />
                        </button>
                      )}

                      {/* ARCHIVE (only if not archived) */}
                      {!dept.is_deleted && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => confirmDelete(dept.id)}
                          title="Archive Department"
                        >
                          {/* 🗑️ */}
                          <Trash2 size={16} />
                        </button>
                      )}

                      {/* RESTORE (only if archived) */}
                      {dept.is_deleted && (
                        <button
                          className="btn-icon btn-success"
                          onClick={() => {
                            setRestoreId(dept.id);
                            setShowRestoreModal(true);
                          }}
                          title="Restore Department"
                        >
                          {/* ♻️ */}
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <svg
              className="empty-icon"
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
            >
              <path
                d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M32 20V32L40 40"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="empty-title">No departments found</h3>
            <p className="empty-text">
              {searchQuery || filterStatus !== "all" || filterType !== "all"
                ? "Try adjusting your search or filters"
                : "Get started by creating your first department"}
            </p>
            {!searchQuery && filterStatus === "all" && filterType === "all" && (
              <button
                className="btn-primary"
                onClick={() => setShowModal(true)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3.33334V12.6667M3.33334 8H12.6667"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Create Your First Department
              </button>
            )}
          </div>
        )}
      </div>
      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay") handleModalClose();
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-header-content">
                <h2 className="modal-title">
                  {editingDept ? "Edit Department" : "Create New Department"}
                </h2>
                <p className="modal-subtitle">
                  {editingDept
                    ? "Update department information and settings"
                    : "Add a new department to your production workflow"}
                </p>
              </div>
              <button
                className="btn-close"
                onClick={handleModalClose}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* SECTION 1: Basic Information */}
                <div className="form-section">
                  <h3 className="section-title">
                    <svg
                      className="section-icon"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Basic Information
                  </h3>
                  <p className="section-description">
                    Essential details to identify and categorize this department
                  </p>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label required">
                        <svg
                          className="form-label-icon"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        Department Name
                      </label>
                      <input
                        type="text"
                        className={`form-input ${
                          formErrors.name ? "error" : ""
                        }`}
                        placeholder="e.g., Embroidery, Quality Control"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                      {formErrors.name && (
                        <span className="error-text">{formErrors.name}</span>
                      )}
                      <span className="field-helper">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        This will be displayed across the system
                      </span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <svg
                          className="form-label-icon"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Department Code
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Auto-generated"
                        value={formData.code}
                        disabled={!editingDept}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            code: e.target.value,
                          }))
                        }
                      />
                      <span className="field-helper">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Automatically generated from department name
                      </span>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">
                        <svg
                          className="form-label-icon"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                        </svg>
                        Department Type
                      </label>
                      <select
                        className={`form-select ${
                          formErrors.type ? "error" : ""
                        }`}
                        value={formData.type}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                      >
                        <option value="Production">Production</option>
                        <option value="Quality">Quality</option>
                        <option value="Finishing">Finishing</option>
                        <option value="Maintenance">Maintenance</option>
                        <option value="Planning">Planning</option>
                        <option value="Logistics">Logistics</option>
                      </select>
                      {formErrors.type && (
                        <span className="error-text">{formErrors.type}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Parent Department
                        <span className="optional-tag">(optional)</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.parentId}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            parentId: e.target.value,
                          }))
                        }
                      >
                        <option value="">None (Top Level)</option>
                        {departments
                          .filter((d) => d.id !== editingDept?.id)
                          .map((dept) => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name} ({dept.code})
                            </option>
                          ))}
                      </select>
                      <span className="field-helper">
                        Select if this is a sub-department
                      </span>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Resource & Costing */}
                <div className="form-section">
                  <h3 className="section-title">
                    <svg
                      className="section-icon"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Resource & Costing
                  </h3>
                  <p className="section-description">
                    Configure production capacity and operational costs
                  </p>

                  <div className="form-grid-3">
                    <div className="form-group">
                      <label className="form-label">
                        Cost Per Hour{" "}
                        <span className="optional-tag">(Optional)</span>
                      </label>
                      <div className="input-with-unit">
                        <input
                          type="number"
                          className={`form-input ${
                            formErrors.costPerHour ? "error" : ""
                          }`}
                          placeholder="500"
                          value={formData.costPerHour}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              costPerHour: e.target.value,
                            }))
                          }
                          min="0"
                          step="0.01"
                        />
                        <div className="input-unit">$ / hour</div>
                      </div>

                      {formErrors.costPerHour ? (
                        <span className="error-text">
                          {formErrors.costPerHour}
                        </span>
                      ) : (
                        <span className="field-helper muted">
                          Used for production costing and profitability analysis
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Daily Capacity{" "}
                        <span className="optional-tag">(Optional)</span>
                      </label>
                      <div className="input-with-unit">
                        <input
                          type="number"
                          className={`form-input ${
                            formErrors.capacity ? "error" : ""
                          }`}
                          placeholder="50"
                          value={formData.capacity}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              capacity: e.target.value,
                            }))
                          }
                          min="1"
                        />

                        <div className="input-unit">units</div>
                      </div>

                      {formErrors.capacity ? (
                        <span className="error-text">
                          {formErrors.capacity}
                        </span>
                      ) : (
                        <span className="field-helper muted">
                          Used for production costing and profitability analysis
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      {/* <label className="form-label">
                        Cost Center
                        <span className="optional-tag">(optional)</span>
                      </label> */}

                      <label className="form-label">
                        Cost Center{" "}
                        <span className="optional-tag">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="CC-001"
                        value={formData.costCenter}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            costCenter: e.target.value,
                          }))
                        }
                      />
                      <span className="field-helper muted">
                        Will be used in future planning & costing
                      </span>
                    </div>
                  </div>

                  <div className="info-box">
                    <svg
                      className="info-box-icon"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="info-box-content">
                      <p className="info-box-title">Costing Information</p>
                      <p className="info-box-text">
                        These values are used for production planning, job
                        costing, and capacity scheduling
                      </p>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: Operations */}
                <div className="form-section">
                  <h3 className="section-title">
                    <svg
                      className="section-icon"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Operations
                  </h3>
                  <p className="section-description">
                    Manage operational details and assignments
                  </p>

                  <div className="form-grid">
                    <div className="form-col-span-2">
                      <DepartmentUserAssignments
                        userOptions={eligibleProductionUsers}
                        usersLoading={usersLoading}
                        usersError={usersError}
                        managerValue={formData.manager}
                        onManagerChange={(managerId) =>
                          setFormData((prev) => ({
                            ...prev,
                            manager: normalizeUserId(managerId) || "",
                          }))
                        }
                        selectedOperatorIds={selectedProductionOperatorIds}
                        onOperatorIdsChange={setSelectedProductionOperatorIds}
                        operatorMappingsLoading={operatorMappingsLoading}
                        operatorMappingError={operatorMappingError}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Location
                        <span className="optional-tag">(optional)</span>
                      </label>
                      <div className="input-group">
                        <svg
                          className="input-icon-left"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <input
                          type="text"
                          className="form-input input-with-icon-left"
                          placeholder="e.g., Floor 1, Building A"
                          value={formData.location}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              location: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="form-group form-col-span-2">
                      {/* <label className="form-label">
                        Operating Hours
                        <span className="optional-tag">(optional)</span>
                      </label> */}

                      <label className="form-label">
                        Operating Hours{" "}
                        <span className="optional-tag">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g., 9:00 AM - 6:00 PM"
                        value={formData.operatingHours}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            operatingHours: e.target.value,
                          }))
                        }
                      />

                      <span className="field-helper muted">
                        Will be used in future planning & costing
                      </span>
                    </div>

                    <div className="form-group form-col-span-2">
                      <label className="form-label">
                        Description
                        <span className="optional-tag">(optional)</span>
                      </label>
                      <textarea
                        className="form-textarea"
                        rows="3"
                        placeholder="Add notes about this department's responsibilities and workflow..."
                        value={formData.description}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 4: Status */}
                <div className="form-section">
                  <div className="toggle-group">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <div className="toggle-content">
                      <div className="toggle-title">Active Department</div>
                      <div className="toggle-description">
                        Active departments appear in production planning and can
                        be assigned to jobs. Inactive departments are hidden
                        from new assignments.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <div className="modal-footer">
              <div className="modal-footer-left">
                {/* {editingDept && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to discard changes?"
                        )
                      ) {
                        handleModalClose();
                      }
                    }}
                  >
                    Reset Form
                  </button>
                )} */}
              </div>
              <div className="modal-footer-right">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button
                  // type="submit"
                  type="button"
                  className="btn-primary"
                  disabled={
                    loading ||
                    usersLoading ||
                    Boolean(usersError) ||
                    operatorMappingsLoading ||
                    Boolean(operatorMappingError)
                  }
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      {editingDept ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      {editingDept ? "Update Department" : "Create Department"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay")
              setShowDeleteModal(false);
          }}
        >
          <div className="modal-content modal-small">
            <div className="modal-header">
              <h2 className="modal-title">Delete Department</h2>
              <button
                className="btn-close"
                onClick={() => setShowDeleteModal(false)}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="modal-body text-center">
              <div className="delete-icon-wrapper">
                <svg
                  className="delete-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 6H21M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6H19Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="delete-title">Are you sure?</h3>
              <p className="delete-text">
                This action cannot be undone. This will permanently delete the
                department and remove all associated data.
              </p>
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left"></div>
              <div className="modal-footer-right">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                >
                  Delete Department
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* RESTORE CONFIRMATION MODAL */}
      {showRestoreModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay")
              setShowRestoreModal(false);
          }}
        >
          <div className="modal-content modal-small">
            <div className="modal-header">
              <h2 className="modal-title">Restore Department</h2>
              <button
                className="btn-close"
                onClick={() => setShowRestoreModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body text-center">
              <h3 className="delete-title">Restore this department?</h3>
              <p className="delete-text">
                The department will become active again and available for
                planning.
              </p>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowRestoreModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-success"
                onClick={() => {
                  handleRestore(restoreId);
                  setShowRestoreModal(false);
                }}
              >
                Restore Department
              </button>
            </div>
          </div>
        </div>
      )}
      {/* VIEW MODAL */}
      {showViewModal && viewDept && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content modal-view-dept" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="modal-header border-bottom">
              <div className="d-flex flex-column flex-grow-1">
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <h2 className="modal-title mb-0">{viewDept.name}</h2>
                  <div className="d-flex align-items-center gap-2">
                    {/* <button 
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                      onClick={() => handleCopyToClipboard(viewDept.code, 'Department code copied')}
                    >
                      <code className="text-muted small">{viewDept.code}</code>
                      <Copy size={14} />
                    </button> */}
                    <span className={`badge bg-${viewDept.is_deleted ? 'secondary' : viewDept.status === 'Active' ? 'success' : 'warning'}`}>
                      {viewDept.is_deleted ? 'Archived' : viewDept.status}
                    </span>
                  </div>
                </div>
                <p className="text-muted small mb-0">
                  {viewDept.type} Department • Last updated {getRelativeTime(viewDept.audit?.history?.[viewDept.audit.history.length - 1]?.at)}
                </p>
              </div>
              
              <div className="d-flex align-items-center gap-2 ms-3">
                {!viewDept.is_deleted && (
                  <button 
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      setShowViewModal(false);
                      handleEdit(viewDept);
                    }}
                    title="Edit Department"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                {/* <button 
                  className="btn btn-sm btn-light"
                  onClick={() => toast('Export functionality coming soon')}
                  title="Export Data"
                >
                  <Download size={16} />
                </button> */}

                {/* <button
        className="btn btn-sm btn-light"
        onClick={() => toast.loading("Export functionality coming soon", {
          duration: 2500,
        })}
        title="Export Data"
      >
        <Download size={16} />
      </button> */}
              


                <button
        className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
        onClick={() => {
          setShowViewModal(false);
          setViewDept(null);
        }}
        title="Close"
        style={{ width: '32px', height: '32px', padding: '0' }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="dept-view-tabs">
              <button 
                className={`dept-view-tab ${activeViewTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveViewTab('details')}
              >
                <FileText size={16} />
                <span>Details</span>
              </button>
              <button 
                className={`dept-view-tab ${activeViewTab === 'audit' ? 'active' : ''}`}
                onClick={() => setActiveViewTab('audit')}
              >
                <Clock size={16} />
                <span>Audit History</span>
                {viewDept.audit?.history?.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-2">{viewDept.audit.history.length}</span>
                )}
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body bg-light">
              
              {/* Details Tab */}
              {activeViewTab === 'details' && (
                <div className="dept-view-details">
                  
                  {/* Overview Section */}
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted fw-semibold mb-3 d-flex align-items-center gap-2">
                      <Building2 size={18} className="text-primary" />
                      Overview
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-primary bg-opacity-10 text-primary">
                                <FileText size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Code</div>
                                <div className="fw-semibold">{viewDept.code || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-info bg-opacity-10 text-info">
                                <Package size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Type</div>
                                <div className="fw-semibold">{viewDept.type || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-success bg-opacity-10 text-success">
                                {viewDept.status === 'Active' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Status</div>
                                <div className="fw-semibold">{viewDept.is_deleted ? 'Archived' : viewDept.status}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-secondary bg-opacity-10 text-secondary">
                                <User size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Manager</div>
                                <div className="fw-semibold">
                                  {getDepartmentManagerDisplayName(viewDept.manager, eligibleProductionUsers)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resources & Costing Section */}
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted fw-semibold mb-3 d-flex align-items-center gap-2">
                      <DollarSign size={18} className="text-primary" />
                      Resources & Costing
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6 col-lg-4">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-warning bg-opacity-10 text-warning">
                                <DollarSign size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Cost Per Hour</div>
                                <div className="fw-bold text-primary">{formatCurrency(viewDept.costPerHour)}/hr</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 col-lg-4">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-success bg-opacity-10 text-success">
                                <Package size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Daily Capacity</div>
                                <div className="fw-bold text-primary">{viewDept.capacity || '—'} units/day</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 col-lg-4">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-info bg-opacity-10 text-info">
                                <FileText size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Cost Center</div>
                                <div className="fw-semibold">{viewDept.costCenter  || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Operations Section */}
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted fw-semibold mb-3 d-flex align-items-center gap-2">
                      <Clock size={18} className="text-primary" />
                      Operations
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-danger bg-opacity-10 text-danger">
                                <MapPin size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Location</div>
                                <div className="fw-semibold">{viewDept.location || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-start gap-3">
                              <div className="dept-info-icon bg-primary bg-opacity-10 text-primary">
                                <Clock size={20} />
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase mb-1">Operating Hours</div>
                                <div className="fw-semibold">{viewDept.operatingHours  || '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {viewDept.description && (
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted fw-semibold mb-3 d-flex align-items-center gap-2">
                        <FileText size={18} className="text-primary" />
                        Description
                      </h6>
                      <div className="card border-0 shadow-sm">
                        <div className="card-body">
                          <p className="mb-0 text-secondary">{viewDept.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Archive Information */}
                  {viewDept.is_deleted && viewDept.audit?.archived_reason && (
                    <div className="alert alert-warning d-flex align-items-start gap-3 border-0 shadow-sm">
                      <Archive size={24} className="flex-shrink-0 mt-1" />
                      <div>
                        <div className="fw-semibold mb-1">Archive Reason</div>
                        <div className="small">{viewDept.audit.archived_reason}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Audit History Tab */}
              {activeViewTab === 'audit' && (
                <div className="dept-view-audit">
                  {viewDept.audit?.history && viewDept.audit.history.length > 0 ? (
                    <div className="audit-timeline">
                      {[...viewDept.audit.history].reverse().map((item, idx) => {
                        const config = getActionConfig(item.action);
                        const IconComponent = config.icon;
                        
                        return (
                          <div key={idx} className="audit-timeline-item">
                            <div className="audit-timeline-marker">
                              <div className={`audit-timeline-dot bg-${config.color} bg-opacity-10 border-${config.color}`}>
                                <IconComponent size={20} className={`text-${config.color}`} />
                              </div>
                              {idx < viewDept.audit.history.length - 1 && (
                                <div className="audit-timeline-line"></div>
                              )}
                            </div>
                            <div className="audit-timeline-content card border-0 shadow-sm">
                              <div className="card-body">
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                  <div>
                                    <span className={`badge bg-${config.color} text-uppercase`}>
                                      {config.label}
                                    </span>
                                    <span className="text-muted small ms-2">{getRelativeTime(item.at)}</span>
                                  </div>
                                  <div className="text-end">
                                    <div className="small text-muted d-flex align-items-center gap-1">
                                      <User size={14} />
                                      User #{item.by}
                                    </div>
                                    <span className="badge bg-light text-dark small">{item.by_role}</span>
                                  </div>
                                </div>
                                
                                {item.action === 'UPDATE' && item.changes && (
                                  <div className="audit-changes">
                                    {Object.entries(item.changes).map(([field, change]) => (
                                      <div key={field} className="audit-change-item">
                                        <div className="small text-uppercase text-muted fw-semibold mb-2">
                                          {field.replace(/_/g, ' ')}
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <code className="audit-old-value">{String(change.from)}</code>
                                          <TrendingUp size={14} className="text-muted" />
                                          <code className="audit-new-value">{String(change.to)}</code>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {item.action === 'CREATE' && item.snapshot && (
                                  <div className="audit-snapshot">
                                    <div className="small text-uppercase text-muted fw-semibold mb-2">
                                      Initial Configuration
                                    </div>
                                    <div className="row g-2">
                                      {Object.entries(item.snapshot).slice(0, 6).map(([key, value]) => (
                                        <div key={key} className="col-md-6">
                                          <div className="p-2 bg-light rounded">
                                            <div className="small text-muted">{key.replace(/_/g, ' ')}</div>
                                            <code className="small">{String(value)}</code>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {item.action === 'ARCHIVE' && item.reason && (
                                  <div className="alert alert-warning small mb-0">
                                    <strong>Reason:</strong> {item.reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <Clock size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No History Available</h5>
                      <p className="text-muted small">Audit trail will appear here once changes are made.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
